import {useTranslation} from 'react-i18next';
import {Modal} from '../../../../../../components/modal/modal';
import {translations} from '../../../../../../i18n/translations';
import FlatInput from '../../../../../../components/flat-input/flat-input';
import {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import {FlatSelectDropdown} from '../../../../../../components/flat-select-dropdown/flat-select-dropdown';
import {DeviceType} from '../../../../../../backend/enums/device-type';
import {Device} from '../../../../../../backend/interfaces/device';

type QuickAccessAddModalProps = {
  data?: {
    name: string;
    type: DeviceType;
  };
  submitting: boolean;
  devices?: Device[];
  onClose: () => void;
  onSave: (name: string, type: DeviceType) => void;
};

export default function QuickAccessAddModal({
  data,
  submitting,
  devices,
  onClose,
  onSave,
}: QuickAccessAddModalProps) {
  const {t} = useTranslation();
  const [name, setName] = useState<string>(data?.name || '');
  const [type, setType] = useState<DeviceType | undefined>(data?.type || undefined);
  const [errorName, setErrorName] = useState<string>();
  const [deviceTypes] = useState<DeviceType[]>(
    Object.keys(translations.device_types).map((key) => (key as DeviceType) || [])
  );
  const [deviceNames, setDeviceNames] = useState<string[]>([]);

  useEffect(() => {
    if (devices) {
      const names = devices.map((device) => device.number).filter((name) => name) as string[];
      setDeviceNames(names);
    }
  }, [devices]);

  return (
    <Modal
      open
      maxWidth="sm"
      fullWidth
      preventCloseClick
      title={
        data
          ? `${t(translations.office.professional_caregiver.settings.quick_access.edit_modal.title)} ${data.name}`
          : t(translations.office.professional_caregiver.settings.quick_access.activate_modal.title)
      }
      onClose={onClose}
      className="occurrences-add-modal"
      actions={[
        {
          label: t(translations.modal.cancel),
          type: 'destructive',
          onClick: onClose,
          disabled: submitting,
        },
        {
          label: t(translations.device_form.save_button),
          type: 'nondestructive',
          onClick: () => {
            if (data && type && name && (data.name !== name || data.type !== type)) {
              onSave(name, type);
            } else if (name && type) {
              onSave(name, type);
            }
          },
          disabled: !!errorName || !name || !type || submitting,
          loading: submitting,
        },
      ]}
    >
      <FlatInput
        className="occurrences-add-modal-name"
        required
        label={t(
          translations.office.professional_caregiver.settings.quick_access.activate_modal
            .device_name
        )}
        value={name}
        counter={{current: name.length, max: 35}}
        onChange={(event) => {
          setName(event.target.value);
          if (event.target.value.length >= 35) {
            setErrorName(
              t(translations.office.professional_caregiver.settings.occurrences.name_error, {
                max: 35,
              })
            );
          } else if (deviceNames.includes(event.target.value)) {
            setErrorName(
              t(
                translations.office.professional_caregiver.settings.quick_access.activate_modal
                  .name_repeat_error
              )
            );
          } else {
            setErrorName(undefined);
          }
        }}
        errors={errorName}
      />
      <Box className="occurrences-add-modal-notify">
        <FlatSelectDropdown
          required
          label={t(
            translations.office.professional_caregiver.settings.quick_access.activate_modal
              .device_type
          )}
          onChange={(option) => setType(option.key as DeviceType)}
          value={type ? {key: type, label: t(translations.device_types[type])} : undefined}
          options={deviceTypes.map((type) => {
            return {
              label: t(translations.device_types[type]),
              key: type,
            };
          })}
        />
      </Box>
    </Modal>
  );
}
